/**
 * Project Service
 * Handles project-related database operations
 */

import { COLLECTIONS, SCHEMAS } from '../config.js'

import { BaseService } from './BaseService.js'

export class ProjectService extends BaseService {
  constructor() {
    super(COLLECTIONS.PROJECTS)
  }

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  async createProject(projectData) {
    const validation = this.validateData(projectData, SCHEMAS[COLLECTIONS.PROJECTS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Set default status if not provided
    if (!projectData.status) {
      projectData.status = 'active'
    }

    return await this.create(projectData)
  }

  /**
   * Update project
   * @param {string} projectId - Project ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, updateData) {
    const validation = this.validateData(updateData, SCHEMAS[COLLECTIONS.PROJECTS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    return await this.update(projectId, updateData)
  }

  /**
   * Get projects by team
   * @param {string} teamId - Team ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of projects
   */
  async getByTeam(teamId, options = {}) {
    return await this.findBy('teamId', teamId, options)
  }

  /**
   * Get projects by creator
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of projects
   */
  async getByCreator(userId, options = {}) {
    return await this.findBy('created_by', userId, options)
  }

  /**
   * Get projects by status
   * @param {string} status - Project status
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of projects
   */
  async getByStatus(status, options = {}) {
    return await this.findBy('status', status, options)
  }

  /**
   * Get active projects
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of active projects
   */
  async getActive(options = {}) {
    return await this.getByStatus('active', options)
  }

  /**
   * Archive project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async archive(projectId) {
    return await this.update(projectId, { status: 'archived' })
  }

  /**
   * Activate project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async activate(projectId) {
    return await this.update(projectId, { status: 'active' })
  }

  /**
   * Search projects
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching projects
   */
  async searchProjects(searchTerm, options = {}) {
    const searchOptions = {
      fields: ['name', 'description'],
      ...options
    }
    return await this.search(searchTerm, searchOptions)
  }

  /**
   * Get project statistics
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project statistics
   */
  async getStatistics(projectId) {
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }

    // This would typically involve other services to get counts
    // For now, return basic project info
    return {
      id: project.id,
      name: project.name,
      status: project.status,
      teamId: project.teamId,
      createdBy: project.created_by,
      createdAt: project.created_at,
      lastUpdated: new Date()
    }
  }

  /**
   * Get comprehensive project overview statistics
   * Fetches all project stats in a single API call to reduce HTTP requests
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Comprehensive project overview statistics
   */
  async getOverviewStatistics(projectId) {
    await this._ensureDatabaseService()
    
    try {
      // Use the database adapter's makeRequest to ensure authentication headers are included
      const projectData = await (this as any).db.adapter.makeRequest(`/projects/${projectId}?include=overview`, {
        method: 'GET'
      })
      
      // Extract the overview data
      const { overview } = projectData
      
      // Ensure overview exists and has the expected structure
      if (!overview) {
        console.error('❌ Overview data not found in response:', projectData)
        throw new Error('Overview data not found in response')
      }
      
      return {
        features: overview.features?.length || 0,
        testCases: overview.testCases?.length || 0,
        testRuns: overview.testRuns?.length || 0,
        bugs: overview.bugs?.length || 0,
        folders: overview.folders?.length || 0,
        users: overview.users?.length || 0,
        data: {
          features: overview.features || [],
          testCases: overview.testCases || [],
          testRuns: overview.testRuns || [],
          bugs: overview.bugs || [],
          folders: overview.folders || [],
          users: overview.users || []
        }
      }
    } catch (error) {
      console.error('❌ Error fetching overview:', error)
      throw error
    }
  }

  /**
   * Get all project statistics
   * @returns {Promise<Object>} All projects statistics
   */
  async getAllStatistics() {
    const projects = await this.getAll()
    const statusCounts = {}
    const teamCounts = {}

    projects.forEach(project => {
      // Count by status
      statusCounts[project.status] = (statusCounts[project.status] || 0) + 1

      // Count by team
      teamCounts[project.teamId] = (teamCounts[project.teamId] || 0) + 1
    })

    return {
      total: projects.length,
      statusCounts,
      teamCounts,
      lastUpdated: new Date()
    }
  }

  /**
   * Get projects by user access
   * @param {string} userId - User ID
   * @param {string} teamId - Team ID (optional)
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of accessible projects
   */
  async getByUserAccess(userId, teamId = null, options = {}) {
    if (teamId) {
      // Get projects for specific team
      return await this.getByTeam(teamId, options)
    } else {
      // Get all projects (in a real app, this would be filtered by user permissions)
      return await this.getAll(options)
    }
  }

  /**
   * Soft delete a project and cascade to all its resources
   * @param {string} projectId - Project ID
   * @param {string} deletedBy - User ID who deleted the project
   * @param {Object} options - Deletion options
   * @param {boolean} options.cascadeToResources - Whether to cascade delete to resources (default: true)
   * @returns {Promise<Object>} Deletion summary
   */
  async softDelete(projectId, deletedBy, options: any = {}) {
    const { cascadeToResources = true } = options
    
    
    try {
      // Get the project first
      const project = await this.getById(projectId)
      if (!project) {
        throw new Error('Project not found')
      }
      
      const summary = {
        projectId,
        projectName: project.name,
        deletedAt: new Date(),
        deletedBy,
        resourcesDeleted: {
          features: 0,
          testCases: 0,
          testRuns: 0,
          bugs: 0,
          folders: 0
        }
      }
      
      // Use single batch operation for project + all resources
      if (cascadeToResources) {
        const cascadeSummary = await this._cascadeDeleteResources(projectId, deletedBy)
        summary.resourcesDeleted = cascadeSummary.resourcesDeleted
      } else {
        // Just delete the project if cascade is disabled
        await this.update(projectId, {
          is_deleted: true,
          deleted_at: new Date(),
          deleted_by: deletedBy,
          updated_at: new Date()
        })
      }
      
      return summary
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Cascade delete resources for a project (internal method)
   * @param {string} projectId - Project ID
   * @param {string} deletedBy - User ID who deleted the project
   * @returns {Promise<Object>} Deletion summary
   */
  async _cascadeDeleteResources(projectId, deletedBy) {
    const summary = {
      resourcesDeleted: {
        features: 0,
        testCases: 0,
        testRuns: 0,
        bugs: 0,
        folders: 0
      }
    }
    
    try {
      // Import all required services
      const { 
        featureService, 
        testCaseService, 
        testRunService, 
        bugService,
        folderService 
      } = await import('./index.js')
      
      // Fetch all resources for the project
      const [features, testCases, testRuns, bugs, folders] = await Promise.all([
        featureService.getByProject(projectId),
        testCaseService.findBy('project_id', projectId),
        testRunService.findBy('project_id', projectId),
        bugService.findBy('project_id', projectId),
        folderService.getByProject(projectId)
      ])
      
      // Filter out already deleted items
      const activeFeatures = features.filter(f => !f.is_deleted)
      const activeTestCases = testCases.filter(tc => !tc.is_deleted)
      const activeTestRuns = testRuns.filter(tr => !tr.is_deleted)
      const activeBugs = bugs.filter(b => !b.is_deleted)
      const activeFolders = folders.filter(f => !f.is_deleted)
      
      // Prepare batch operations for project + all resources
      const allOperations = []
      
      // 0. Soft delete the project itself in the same batch
      allOperations.push({
        type: 'update',
        collection: 'projects',
        id: projectId,
        data: {
          is_deleted: true,
          deleted_at: new Date(),
          deleted_by: deletedBy,
          updated_at: new Date()
        }
      })
      
      // 1. Soft delete all features
      activeFeatures.forEach(feature => {
        allOperations.push({
          type: 'update',
          collection: 'features',
          id: feature.id,
          data: {
            is_deleted: true,
            deleted_at: new Date(),
            deleted_by: deletedBy,
            updated_at: new Date()
          }
        })
      })
      
      // 2. Soft delete all test cases
      activeTestCases.forEach(testCase => {
        allOperations.push({
          type: 'update',
          collection: 'test_cases',
          id: testCase.id,
          data: {
            is_deleted: true,
            deleted_at: new Date(),
            deleted_by: deletedBy,
            updated_at: new Date()
          }
        })
      })
      
      // 3. Soft delete all test runs
      activeTestRuns.forEach(testRun => {
        allOperations.push({
          type: 'update',
          collection: 'test_runs',
          id: testRun.id,
          data: {
            is_deleted: true,
            deleted_at: new Date(),
            deleted_by: deletedBy,
            updated_at: new Date()
          }
        })
      })
      
      // 4. Soft delete all bugs
      activeBugs.forEach(bug => {
        allOperations.push({
          type: 'update',
          collection: 'bugs',
          id: bug.id,
          data: {
            is_deleted: true,
            deleted_at: new Date(),
            deleted_by: deletedBy,
            updated_at: new Date()
          }
        })
      })
      
      // 5. Soft delete all folders
      activeFolders.forEach(folder => {
        allOperations.push({
          type: 'update',
          collection: 'folders',
          id: folder.id,
          data: {
            ...folder,
            is_deleted: true,
            deleted_at: new Date(),
            deleted_by: deletedBy,
            updated_at: new Date()
          }
        })
      })
      
      // Execute all operations in a single batch call
      if (allOperations.length > 0) {
        await projectService.batch(allOperations)
      }
      
      // Update summary counts
      summary.resourcesDeleted.features = activeFeatures.length
      summary.resourcesDeleted.testCases = activeTestCases.length
      summary.resourcesDeleted.testRuns = activeTestRuns.length
      summary.resourcesDeleted.bugs = activeBugs.length
      summary.resourcesDeleted.folders = activeFolders.length
      
      return summary
      
    } catch (error) {
      // Silent error handling
      return summary
    }
  }

  /**
   * Restore a soft-deleted project
   * @param {string} projectId - Project ID
   * @param {string} restoredBy - User ID who restored the project
   * @param {Object} options - Restoration options
   * @param {boolean} options.restoreResources - Whether to restore resources (default: true)
   * @returns {Promise<Object>} Restoration summary
   */
  async restore(projectId, restoredBy, options: any = {}) {
    const { restoreResources = true } = options
    
    
    try {
      // Get the project first
      const project = await this.getById(projectId)
      if (!project) {
        throw new Error('Project not found')
      }
      
      if (!project.is_deleted) {
        throw new Error('Project is not deleted')
      }
      
      const summary = {
        projectId,
        projectName: project.name,
        restoredAt: new Date(),
        restoredBy,
        resourcesRestored: {
          features: 0,
          testCases: 0,
          testRuns: 0,
          bugs: 0,
          folders: 0
        }
      }
      
      // Optionally restore all resources using single batch
      if (restoreResources) {
        
        // Import all required services
        const { 
          featureService, 
          testCaseService, 
          testRunService, 
          bugService,
          folderService 
        } = await import('./index.js')
        
        // Fetch all resources for the project
        const [allFeatures, allTestCases, allTestRuns, allBugs, allFolders] = await Promise.all([
          featureService.findBy('project_id', projectId),
          testCaseService.findBy('project_id', projectId),
          testRunService.findBy('project_id', projectId),
          bugService.findBy('project_id', projectId),
          folderService.getByProject(projectId)
        ])
        
        // Filter for deleted items only
        const deletedFeatures = allFeatures.filter(f => f.is_deleted)
        const deletedTestCases = allTestCases.filter(tc => tc.is_deleted)
        const deletedTestRuns = allTestRuns.filter(tr => tr.is_deleted)
        const deletedBugs = allBugs.filter(b => b.is_deleted)
        const deletedFolders = allFolders.filter(f => f.is_deleted)
        
        // Prepare batch operations for project + all resources
        const allOperations = []
        
        // 0. Restore the project itself in the same batch
        allOperations.push({
          type: 'update',
          collection: 'projects',
          id: projectId,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date()
          }
        })
        
        // 1. Restore all features
        deletedFeatures.forEach(feature => {
          allOperations.push({
            type: 'update',
            collection: 'features',
            id: feature.id,
            data: {
              is_deleted: false,
              deleted_at: null,
              deleted_by: null,
              updated_at: new Date()
            }
          })
        })
        
        // 2. Restore all test cases
        deletedTestCases.forEach(testCase => {
          allOperations.push({
            type: 'update',
            collection: 'test_cases',
            id: testCase.id,
            data: {
              is_deleted: false,
              deleted_at: null,
              deleted_by: null,
              updated_at: new Date()
            }
          })
        })
        
        // 3. Restore all test runs
        deletedTestRuns.forEach(testRun => {
          allOperations.push({
            type: 'update',
            collection: 'test_runs',
            id: testRun.id,
            data: {
              is_deleted: false,
              deleted_at: null,
              deleted_by: null,
              updated_at: new Date()
            }
          })
        })
        
        // 4. Restore all bugs
        deletedBugs.forEach(bug => {
          allOperations.push({
            type: 'update',
            collection: 'bugs',
            id: bug.id,
            data: {
              is_deleted: false,
              deleted_at: null,
              deleted_by: null,
              updated_at: new Date()
            }
          })
        })
        
        // 5. Restore all folders
        deletedFolders.forEach(folder => {
          allOperations.push({
            type: 'update',
            collection: 'folders',
            id: folder.id,
            data: {
              ...folder,
              is_deleted: false,
              deleted_at: null,
              deleted_by: null,
              updated_at: new Date()
            }
          })
        })
        
        // Execute all operations in a single batch call
        if (allOperations.length > 0) {
          await projectService.batch(allOperations)
        }
        
        // Update summary counts
        summary.resourcesRestored.features = deletedFeatures.length
        summary.resourcesRestored.testCases = deletedTestCases.length
        summary.resourcesRestored.testRuns = deletedTestRuns.length
        summary.resourcesRestored.bugs = deletedBugs.length
        summary.resourcesRestored.folders = deletedFolders.length
      }
      
      return summary
      
    } catch (error) {
      throw error
    }
  }

  /**
   * Get deleted projects
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted projects
   */
  async getDeleted(options = {}) {
    const allProjects = await this.getAll(options)
    return allProjects.filter(project => project.is_deleted === true)
  }

  /**
   * Get deleted projects by team
   * @param {string} teamId - Team ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted projects
   */
  async getDeletedByTeam(teamId, options = {}) {
    const allProjects = await this.getByTeam(teamId, options)
    return allProjects.filter(project => project.is_deleted === true)
  }
}

export const projectService = new ProjectService()
